import { isNullOrUndefined } from './objectUtils';

// Single home for HEIC/HEIF handling. Conversion happens ONCE at upload time
// (ensureJpegFile in FormItemCamera); the blob-level helpers remain for legacy
// .heic files already stored in Firebase Storage.

export const HEIC_FAMILY_EXTENSIONS = ['heic', 'heif'];

export const getFileExtension = (fileName = '') => {
    const lastDot = fileName.lastIndexOf('.');
    if (lastDot === -1) {
        return '';
    }

    return fileName.substring(lastDot + 1).split('?')[0].toLowerCase();
};

export const isHeicFamilyExtension = (ext = '') => HEIC_FAMILY_EXTENSIONS.includes(ext?.toLowerCase?.() ?? '');

export const isHeicFamilyFile = (fileObj) => {
    const fileType = fileObj?.type?.toLowerCase?.() ?? '';
    const fileExtension = getFileExtension(fileObj?.name ?? '');
    return ['image/heic', 'image/heif'].includes(fileType) || isHeicFamilyExtension(fileExtension);
};

export const toJpegFileName = (fileName = '') => {
    const lastDot = fileName.lastIndexOf('.');
    const baseName = lastDot === -1 ? fileName : fileName.substring(0, lastDot);
    return `${baseName}.jpg`;
};

export const convertHeicFamilyBlobToJpeg = async (blob, quality = 0.92) => {
    // heic-to bundles libheif (over a megabyte minified) - load it on demand so it only downloads
    // when a customer actually uploads a heic file instead of riding in the initial app chunk
    const { heicTo } = await import('heic-to');
    const convertedBlob = await heicTo({ blob, type: 'image/jpeg', quality });
    if (isNullOrUndefined(convertedBlob)) {
        throw new Error('Unable to convert HEIC/HEIF image.');
    }

    return convertedBlob;
};

// Returns the file unchanged unless it is HEIC/HEIF, in which case it is converted
// to a jpeg File (renamed *.jpg) so everything downstream — storage, previews,
// Beluga uploads, doctor views — only ever sees browser-renderable formats.
export const ensureJpegFile = async (fileObj) => {
    if (isNullOrUndefined(fileObj) || !isHeicFamilyFile(fileObj)) {
        return fileObj;
    }

    const convertedBlob = await convertHeicFamilyBlobToJpeg(fileObj);
    return new File([convertedBlob], toJpegFileName(fileObj.name ?? 'photo'), { type: 'image/jpeg' });
};
